import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Shield, MapPin, Loader2, Plus, ChevronRight, Star } from "lucide-react";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";

const VendorDashboard = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get("/vendor/dashboard");
        setData(res.data);
      } catch (err) { logger.error("Fetch error:", err); } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="pt-24 pb-16 px-6 min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  const providers = data?.providers || [];

  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Dashboard</p>
            <h1 className="text-4xl text-[#EDEDED]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Vendor Dashboard
            </h1>
          </div>
          <Link to="/vendor/register" className="btn-gold text-sm inline-flex items-center gap-2" data-testid="add-service-btn">
            <Plus className="w-4 h-4" /> Add Service
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <div className="glass-card p-4 text-center">
            <p className="text-2xl text-[#D4AF37] font-bold">{providers.length}</p>
            <p className="text-xs text-[#71717A]">Services Listed</p>
          </div>
          <div className="glass-card p-4 text-center">
            <p className="text-2xl text-[#22C55E] font-bold">{providers.filter(p => p.is_verified).length}</p>
            <p className="text-xs text-[#71717A]">Verified</p>
          </div>
          <div className="glass-card p-4 text-center">
            <p className="text-2xl text-[#EDEDED] font-bold">{data?.total_leads || 0}</p>
            <p className="text-xs text-[#71717A]">Total Leads</p>
          </div>
        </div>

        {/* Providers List */}
        {providers.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Briefcase className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
            <p className="text-[#A1A1AA] mb-2">No services registered yet.</p>
            <Link to="/vendor/register" className="text-[#D4AF37] text-sm hover:underline">
              Register your first service
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {providers.map(p => (
              <div key={p.provider_id} className="glass-card p-5" data-testid={`vendor-item-${p.provider_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg text-[#EDEDED]">{p.display_name}</h3>
                      {p.is_verified ? (
                        <span className="flex items-center gap-1 text-xs text-[#22C55E] bg-[#22C55E]/10 px-2 py-0.5 rounded-full">
                          <Shield className="w-3 h-3" /> Verified
                        </span>
                      ) : (
                        <span className="text-xs text-[#F59E0B] bg-[#F59E0B]/10 px-2 py-0.5 rounded-full">Pending Review</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                      <span className="capitalize">{p.service_type}</span>
                      <span className="text-[#3F3F46]">|</span>
                      <span className="capitalize">{Array.isArray(p.species) ? p.species.join(", ") : p.species}</span>
                      {(p.location_city || p.location_state) && (
                        <>
                          <span className="text-[#3F3F46]">|</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{[p.location_city, p.location_state].filter(Boolean).join(", ")}</span>
                        </>
                      )}
                    </div>
                    {p.description && <p className="text-sm text-[#71717A] mt-2 line-clamp-2">{p.description}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-lg text-[#D4AF37] font-bold">{p.lead_count || 0}</p>
                    <p className="text-xs text-[#71717A]">Leads</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorDashboard;
